import type { ShopContext } from '$lib/context';
import { type ApiReturnType, type ApiSchemas, shopwareApiClient } from '$lib/api/shopwareApi.js';
import { ApiClientError } from '$lib/api/util/errorInterceptor';
import type { SitemapProductResponse } from '$lib/api/types/sitemapProductResponse.js';
import { undefinedShopContext } from '$lib/layout/shopLayoutServer.js';

type Options = {
    [key in 'taste' | 'location' | 'line' | 'type']: string;
};

export class ProductApi {
    public static async getProduct(
        id: string,
        shopContext: ShopContext,
        queries?: object,
        fetchImplementation?: typeof fetch
    ): Promise<ApiSchemas['Product']> {
        const response: { product: any } = await shopwareApiClient.invoke(
            `readProductDetail post /product/${id}`,
            queries,
            { fetchImplementation, shopContext }
        );

        if (!response.product) {
            const error = new ApiClientError(undefined);
            error.status = 404;
            error.message = `Product with id "${id}" not found`;

            throw error;
        }

        return response.product as ApiSchemas['Product'];
    }

    public static getProducts(
        criteria: ApiSchemas['Criteria'],
        shopContext: ShopContext,
        fetchImplementation?: typeof fetch
    ): Promise<ApiReturnType<'readProduct'>[]> {
        return shopwareApiClient.invoke('readProduct post /product', criteria, { shopContext, fetchImplementation });
    }

    public static getAllProducts(
        shopContext: ShopContext,
        fetchImplementation?: typeof fetch
    ): Promise<ApiReturnType<'readProduct'>> {
        return shopwareApiClient.invoke(
            'readProduct post /product',
            {
                filter: [
                    {
                        type: 'equals',
                        field: 'parentId',
                        value: null
                    }
                ],
                associations: {
                    cover: {},
                    properties: {},
                    options: {
                        group: {}
                    },
                    children: {}
                }
            },
            { fetchImplementation, shopContext }
        );
    }

    public static getAllProductsReduced(
        shopContext: ShopContext,
        fetchImplementation?: typeof fetch
    ): Promise<ApiReturnType<'readProduct'>> {
        return shopwareApiClient.invoke(
            'readProduct post /product',
            {
                filter: [
                    {
                        type: 'equals',
                        field: 'parentId',
                        value: null
                    },
                    {
                        type: 'range',
                        field: 'childCount',
                        parameters: {
                            gt: 0
                        }
                    }
                ],
                associations: {
                    cover: {},
                    properties: {},
                    children: {
                        associations: {
                            options: {
                                associations: {
                                    group: {}
                                }
                            }
                        }
                    }
                },
                includes: {
                    product: [
                        'id',
                        'name',
                        'translated.name',
                        'calculatedPrice',
                        'children',
                        'childCount',
                        'options',
                        'optionIds',
                        'cover',
                        'group',
                        'productNumber',
                        'properties',
                        'optionsIds'
                    ]
                }
            },
            { fetchImplementation, shopContext }
        );
    }

    public static getAllVariantsOfProduct(
        productId: string,
        shopContext: ShopContext,
        fetchImplementation?: typeof fetch
    ): Promise<ApiReturnType<'readProduct'>> {
        return shopwareApiClient.invoke(
            'readProduct post /product',
            {
                filter: [
                    {
                        type: 'equals',
                        field: 'parentId',
                        value: productId
                    }
                ],
                associations: {
                    cover: {},
                    properties: {},
                    options: {
                        associations: {
                            group: {}
                        }
                    }
                }
            },
            { fetchImplementation, shopContext }
        );
    }

    public static async getProductByNumber(
        productNumber: string,
        shopContext: ShopContext,
        fetchImplementation?: typeof fetch
    ): Promise<ApiSchemas['Product']> {
        const response: any = await shopwareApiClient.invoke(
            'readProduct post /product',
            {
                filter: [{ type: 'equals', value: productNumber, field: 'productNumber' }],
                associations: {
                    media: {},
                    properties: {
                        associations: { group: {} }
                    },
                    options: { associations: { group: {} } }
                }
            },
            { fetchImplementation, shopContext }
        );

        if (!response.elements?.length) {
            const error = new ApiClientError(undefined);
            error.status = 404;
            error.message = `Product with productNumber "${productNumber}" not found`;

            throw error;
        }

        return response.elements[0] as ApiSchemas['Product'];
    }

    // TODO: currently works with ids maybe use readable text in the future
    public static getFilteredParentProducts(
        options: { limit?: number; properties?: string[]; category?: string; exclude?: string },
        shopContext: ShopContext,
        fetchImplementation?: typeof fetch
    ): Promise<ApiReturnType<'readProduct'>> {
        const filter: any = [
            {
                type: 'equals',
                field: 'parentId',
                value: null
            }
        ];

        // WIP
        if (options.properties && options.properties.length > 0) {
            const queries: any[] = [];
            options.properties.forEach((property) => {
                queries.push({
                    type: 'contains',
                    field: 'propertyIds',
                    value: property
                });
            });

            filter.push({ type: 'multi', operator: 'and', queries });
        }

        if (options.category) {
            filter.push({ type: 'contains', field: 'categoryIds', value: options.category });
        }

        if (options.exclude) {
            filter.push({
                type: 'not',
                operator: 'or',
                queries: [
                    {
                        type: 'equals',
                        field: 'id',
                        value: options.exclude
                    }
                ]
            });
        }

        return shopwareApiClient.invoke(
            'readProduct post /product',
            {
                limit: options.limit ?? 16,
                filter,
                associations: {
                    cover: {},
                    properties: {},
                    children: {
                        associations: {
                            options: {
                                associations: {
                                    group: {}
                                }
                            },
                            cover: {}
                        }
                    }
                },
                includes: {
                    product: [
                        'id',
                        'name',
                        'translated.name',
                        'calculatedPrice',
                        'children',
                        'childCount',
                        'options',
                        'optionIds',
                        'cover',
                        'group',
                        'productNumber',
                        'properties',
                        'optionsIds',
                        'customFields'
                    ]
                }
            },
            { fetchImplementation, shopContext }
        );
    }

    public static listProductsForSitemap(
        page: number,
        fetchImplementation: typeof fetch
    ): Promise<SitemapProductResponse> {
        return shopwareApiClient.invoke(
            'readProduct post /product',
            {
                includes: {
                    product: ['id', 'productNumber', 'parentId']
                },
                page,
                limit: 100
            },
            { fetchImplementation, shopContext: undefinedShopContext }
        );
    }
}
